import React from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  formatPhone,
  isEmptyString,
} from '@populateui/utils';
import {
  UserView,
} from 'views/EMR/Settings/Users';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Name from 'pages/Dashboard/components/Name';
import ToolTip from 'core/components/Tooltip';
import Tag from 'core/components/Tag';
import {
  Link,
} from 'react-router-dom';
import {
  helpDeskURL,
} from 'core/constants';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  Row,
} from '@tanstack/react-table';

type NameCellProps = {
  row: Row<UserView>;
};

function NameCell({ row: { original: { isActive, formattedNames, userId } } }: NameCellProps) {
  const src = useUserProfilePicturePath(userId);

  return (
    <ToolTip
      title={(
        <div className="flex flex-col gap-0">
          <span>This user is inactive.</span>
          <span>
            Please
            {' '}
            <Link
              to={helpDeskURL}
              target="_blank"
              className="underline"
              onClick={(e) => { e.stopPropagation(); }}
            >
              contact support
            </Link>
            {' '}
            for assistance.
          </span>
        </div>
      )}
      isInteractive
      hide={isActive}
    >
      <div className="flex items-center gap-2">
        <Name
          {...(formattedNames ?? {})}
          image={{ src }}
        />
        {!isActive && (
          <Tag
            tagName="Inactive"
            className="!bg-gray-200 !text-gray-700"
          />
        )}
      </div>
    </ToolTip>
  );
}

const columns: ColumnDef<UserView>[] = [{
  id: 'name',
  columnTitle: 'Name',
  meta: { autoSized: true },
  minSize: 200,
  cell: ({ row }) => <NameCell row={row} />,
}, {
  id: 'title',
  columnTitle: 'Title',
  size: 180,
  cell: ({ row }) => row.original?.userTitle?.title ?? '',
}, {
  id: 'designation',
  columnTitle: 'Designation',
  size: 220,
  cell: ({ row }) => row.original.designation ?? '--',
}, {
  id: 'email',
  accessorKey: 'email',
  columnTitle: 'Email',
  size: 250,
},
{
  id: 'phoneNumber',
  columnTitle: 'Phone',
  cell: ({ row }) => formatPhone(row.original?.phoneNumber ?? ''),
}, {
  id: 'isDosespotConfigured',
  columnTitle: 'eRx',
  size: 32,
  cell: ({ row }) => (
    <div className="flex justify-center w-full">
      <ToolTip
        hide={!isEmptyString(row.original?.dosespotClinicianId)}
        title="ePrescription account not created. Please contact Populate Support for assistance."
      >
        {!isEmptyString(row.original?.dosespotClinicianId)
          ? (<CheckCircleIcon className="!text-sm text-green-600 cursor-pointer" />) : (
            (<CancelIcon className="!text-sm text-red-600 cursor-not-allowed" />)
          )}
      </ToolTip>
    </div>
  ),
}];

export default columns;
